/**
 * Turning `order_list` rows into `SubmittedOrder` (MYP-53).
 *
 * The same job `normalisePackages` does for packages, and written against rows captured live
 * rather than from the API document — which lists five fields for `order_list` where the live
 * endpoint returns twenty-four on a summary row and one hundred and seventy-three on a detail one.
 *
 * The endpoint serves two different shapes from one path, and which one you get depends on the
 * parameter, not on the count:
 *
 * - `order_list` with no `orderid` (or filtered by `orderno`) returns summary rows: enough for an
 *   order card, with no packages, no address, no timeline and no creation date.
 * - `order_list` with `orderid` returns a single flat record — an order joined to its packages,
 *   with the package columns prefixed `pkg_` and the whole set repeated per package under
 *   `pkgInfo`.
 *
 * `normaliseOrders` handles the first and `normaliseOrderDetail` the second. The screens fetch
 * detail only when a card is expanded: there are 284 orders on the test account, and pre-fetching
 * detail for the list means 284 requests to render one screen.
 */

import { toIsoTimestamp } from './packages-wire.js';
import { receiptPathsFrom } from './receipts.js';
import type { Package } from './packages.js';
import type {
  DeliveryAddress,
  FeeLine,
  OrderablePackage,
  OrderModifyBase,
  OrderPackageLine,
  OrderStatus,
  OrderTimelineEvent,
  PaymentMode,
  SubmittedOrder,
} from './orders.js';

type Row = Readonly<Record<string, unknown>>;

/**
 * The vendor's eighteen order statuses, mapped onto the app's five stages plus `cancelled` and
 * `exception`. Taken from a live `order_status_list` call, not from the document.
 *
 * Two groups of these looked like they might need machinery of their own and do not:
 *
 * - **Partial states** sit at their dominant stage. A part-paid order still has money owing, so
 *   `PARTPACK` belongs under `unpaid`; the row's own label says 部分未付款, which is where the
 *   nuance lives.
 * - **Self-pickup** is a delivery *mode*, not a separate track. `ARRIVEDPICK` is despatched and
 *   `PICKUPED` is in the customer's hands, so `shipped` and `received` hold them as they stand.
 *
 * Only the three genuinely-stopped states need `exception` — see `OrderStatus`.
 */
const STATUS_GROUPS: Readonly<Record<string, OrderStatus>> = {
  ORDERED: 'collecting', // 未收齐 not all collected
  PENDING: 'collecting', // 已收齐 all collected
  OUT: 'collecting', // 已捡单 picked

  PACK: 'unpaid', // 未付款
  PARTPACK: 'unpaid', // 部分未付款

  WFD: 'preparingDespatch', // 等待发货
  PARTWFD: 'preparingDespatch', // 部分待发货

  DESPATCH: 'shipped', // 已发货
  PARTDESPATCH: 'shipped', // 部分发货
  DESPNOPAY: 'shipped', // 直发货 despatched unpaid
  TRANSIT: 'shipped', // 中转中 in transfer
  ARRIVEDPICK: 'shipped', // 到达自提点 at the pickup point

  RECEIPT: 'received', // 已收货
  PICKUPED: 'received', // 已自提 collected in person

  CANCLE: 'cancelled', // 退单 — the vendor's own spelling

  HOLD: 'exception', // 扣货 held
  PROBLEM: 'exception', // 问题订单
  MODIFICATION: 'exception', // 修改申请 modification requested
};

/**
 * A status code's group. An unrecognised code lands in `collecting` — the earliest stage, and the
 * only one whose meaning cannot overstate what has happened. Guessing `shipped` for a code the
 * vendor adds tomorrow would tell a customer their parcel has left when nobody knows that.
 */
export function toOrderStatus(code: string): OrderStatus {
  return STATUS_GROUPS[code.trim().toUpperCase()] ?? 'collecting';
}

/** Every status code this app knows how to group, for tests and for spotting vendor additions. */
export const KNOWN_ORDER_STATUS_CODES: readonly string[] = Object.keys(STATUS_GROUPS);

/**
 * The vendor codes behind one of this app's groups — the reverse of `toOrderStatus`, for asking
 * the server for one stage's orders (MYP-126). `order_list` filters by one code at a time, so a
 * stage with several codes is fetched code by code.
 */
export function orderStatusCodesFor(group: OrderStatus): readonly string[] {
  return KNOWN_ORDER_STATUS_CODES.filter((code) => STATUS_GROUPS[code] === group);
}

/** Which language a status label resolves to. The vendor stores only Chinese and English. */
export type OrderStatusLanguage = 'zh' | 'en';

/** `status_en`/`status_cn` per code, as `order_status_list` returns them. */
export interface OrderStatusEntry {
  readonly code: string;
  readonly labelEn: string;
  readonly labelCn: string;
}

function text(value: unknown): string | undefined {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed === '' ? undefined : trimmed;
  }
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return undefined;
}

function digits(value: unknown): number | undefined {
  const raw = text(value);
  if (raw === undefined) return undefined;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/** A decimal amount on the wire → integer cents, without float drift. */
function cents(value: unknown): number | undefined {
  const amount = digits(value);
  return amount === undefined ? undefined : Math.round(amount * 100);
}

/** Kilograms on the wire → the integer grams the app holds. */
function grams(value: unknown): number | undefined {
  const kg = digits(value);
  return kg === undefined ? undefined : Math.round(kg * 1000);
}

/**
 * Decodes the HTML entities the vendor stores in free text.
 *
 * Addresses come back with `&#039;` where an apostrophe was typed, because the legacy site encoded
 * on the way in and never decodes on the way out. Rendering that raw puts `taman&#039;s` on a
 * courier label. Only the handful of entities actually seen are decoded, plus numeric ones — this
 * is a display fix, not an HTML parser, and nothing here is ever inserted as markup.
 *
 * `&amp;` is unescaped last so `&amp;#039;` — an address that was encoded twice on the way in, and
 * several were — does not become an apostrophe on the first pass and get re-read on the second.
 */
export function decodeEntities(value: string): string {
  return value
    .replace(/&#0?39;|&apos;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&#(\d+);/g, (_, code: string) => String.fromCodePoint(Number(code)))
    .replace(/&amp;/g, '&');
}

function prose(value: unknown): string | undefined {
  const raw = text(value);
  return raw === undefined ? undefined : decodeEntities(raw);
}

/** `order_status_list` rows → the dictionary the adapters resolve labels against. */
export function normaliseOrderStatuses(rows: readonly unknown[]): readonly OrderStatusEntry[] {
  const entries: OrderStatusEntry[] = [];

  for (const row of rows) {
    if (typeof row !== 'object' || row === null) continue;
    const record = row as Row;
    const code = text(record['status']);
    if (code === undefined) continue;

    entries.push({
      code,
      labelEn: text(record['status_en']) ?? code,
      labelCn: text(record['status_cn']) ?? text(record['status_zh']) ?? code,
    });
  }

  return entries;
}

/**
 * The vendor's own words for this order's state.
 *
 * The dictionary first, then the label the row carries itself. That second source matters more
 * than it looks: every `order_list` row ships its own `status_cn`, so a failed dictionary fetch
 * costs a Chinese reader nothing and costs an English one a bare code rather than a blank chip.
 */
function statusLabelFor(
  record: Row,
  code: string,
  statuses: readonly OrderStatusEntry[],
  language: OrderStatusLanguage,
): string {
  const entry = statuses.find((candidate) => candidate.code === code);
  return (language === 'zh' ? entry?.labelCn : entry?.labelEn) ?? text(record['status_cn']) ?? code;
}

/**
 * The fee breakdown, from two parallel delimited strings.
 *
 * The vendor sends labels and amounts as separate lists that have to be zipped back together —
 * and **with different delimiters**: `feename` is semicolon-separated (`"普货空运AR:西;普货空运附加费"`)
 * while `fee` is comma-separated (`"26.00,15.60"`). Splitting both on the same character silently
 * mis-pairs every line, which is the kind of bug that shows a customer the wrong price against the
 * right label — and a label can legitimately contain a comma, so the semicolon is not incidental.
 *
 * A label with no amount is dropped rather than shown as free. Negative amounts are kept: real
 * orders carry `ROUND ADJUSTMENT` and `Discount` lines that are meant to subtract.
 *
 * `feecurrency` is a third parallel list, comma-separated like `fee` (the vendor confirmed the
 * rule in its Q&A: only names use `;`). Each line keeps its own currency when one is given (MYP-22).
 */
export function parseFeeLines(
  feename: unknown,
  fee: unknown,
  feecurrency?: unknown,
): readonly FeeLine[] {
  const labels = (text(feename) ?? '').split(';');
  const amounts = (text(fee) ?? '').split(',');
  const currencies = (text(feecurrency) ?? '').split(',');

  const lines: FeeLine[] = [];
  for (const [index, label] of labels.entries()) {
    const trimmed = decodeEntities(label.trim());
    const amount = cents(amounts[index]);
    if (trimmed === '' || amount === undefined) continue;
    const currency = currencies[index]?.trim();
    lines.push({ label: trimmed, amountCents: amount, ...(currency ? { currency } : {}) });
  }

  return lines;
}

/**
 * The add-on services the warehouse performed, as fee lines.
 *
 * These are billed but are *not* in `feename`/`fee` — they arrive as an `OrderService` array with
 * a name and a fee each, and the vendor's `totalfee` includes them. Appending them here is what
 * makes the breakdown on screen account for the difference between the itemised channel fees and
 * the bill; see `billedTotalCents` for why the total itself is still taken from the vendor.
 */
function serviceFeeLines(value: unknown): readonly FeeLine[] {
  if (!Array.isArray(value)) return [];

  const lines: FeeLine[] = [];
  for (const entry of value) {
    if (typeof entry !== 'object' || entry === null) continue;
    const service = entry as Row;
    const label = prose(service['servicename']) ?? prose(service['name']);
    const amount = cents(service['fee']);
    if (label === undefined || amount === undefined) continue;
    lines.push({ label, amountCents: amount });
  }

  return lines;
}

/** `payment` is MANUAL or AUTO on the wire; anything else is treated as manual, the safer default. */
function paymentMode(value: unknown): PaymentMode {
  return text(value)?.toUpperCase() === 'AUTO' ? 'AUTO' : 'MANUAL';
}

/**
 * What the vendor says the order costs, or undefined while it has not priced it.
 *
 * `totalfee` is `0` on every order the warehouse has not yet weighed and packed — not because
 * those orders are free, but because there is nothing to add up yet. Reading that as RM 0.00 puts
 * a price on screen that the customer will later be asked to beat, so zero is treated as absent
 * and the card falls through to "price pending".
 */
function billedTotal(value: unknown): number | undefined {
  const total = cents(value);
  return total === undefined || total === 0 ? undefined : total;
}

/**
 * One summary row → one `SubmittedOrder`, or `undefined` for a row with no order number.
 *
 * Deliberately partial. `packages`, `address`, `timeline`, `remark` and the payment mode are not
 * on a summary row at all — the endpoint simply does not send those columns without an `orderid` —
 * so they are left empty here and filled by `normaliseOrderDetail` when a card is expanded.
 */
export function normaliseOrder(
  row: unknown,
  statuses: readonly OrderStatusEntry[],
  language: OrderStatusLanguage,
): SubmittedOrder | undefined {
  if (typeof row !== 'object' || row === null) return undefined;
  const record = row as Row;

  const orderNumber = text(record['orderno']);
  if (orderNumber === undefined) return undefined;

  const statusCode = text(record['status']) ?? 'ORDERED';
  const trackingNumber = text(record['airno']);
  const carrier = prose(record['channelname']);

  return {
    orderNumber,
    /*
      A summary row carries no creation date; only the detail record has `createdate`. Left empty
      rather than invented, and `filterOrders` sorts on the order number — which is `YYMMDD-nnnnnn`,
      date-prefixed — so the list is still in date order without this.
    */
    createdAt: '',
    status: toOrderStatus(statusCode),
    statusCode,
    statusLabel: statusLabelFor(record, statusCode, statuses, language),
    vendorOrderId: text(record['id']),
    packages: [],
    channelId: text(record['channelid']),
    carrier,
    // The waybill's weight, which is what the order actually shipped at. Zero until the warehouse
    // has packed it, same as every other number on an unpriced row.
    totalWeightGrams: grams(record['airno_weight']) ?? 0,
    chargeableWeightGrams: grams(record['calcweight']),
    feeLines: parseFeeLines(record['feename'], record['fee'], record['feecurrency']),
    billedTotalCents: billedTotal(record['totalfee']),
    currency: text(record['currency']),
    // `airno` is the waybill; the channel is the nearest thing to a carrier the API names. No
    // latest-update text exists anywhere in the vendor API — that is MYP-52.
    tracking: trackingNumber ? { trackingNumber, carrier } : undefined,
    serviceIds: [],
    // Not on a summary row. MANUAL is the default the create flow uses and the one that asks the
    // customer before taking money, so it is the safe thing to assume until detail says otherwise.
    paymentMode: 'MANUAL',
    couponCode: text(record['codeno']) ?? '',
    remark: '',
  };
}

/** Every usable summary row, in the order the vendor sent them. */
export function normaliseOrders(
  rows: readonly unknown[],
  statuses: readonly OrderStatusEntry[],
  language: OrderStatusLanguage,
): readonly SubmittedOrder[] {
  const orders: SubmittedOrder[] = [];

  for (const row of rows) {
    const parsed = normaliseOrder(row, statuses, language);
    if (parsed) orders.push(parsed);
  }

  return orders;
}

/**
 * The stages the app draws on a timeline, and the vendor date column each reads from.
 *
 * The vendor keeps a date/operator pair per workflow step rather than an event log, so the
 * timeline is reconstructed by asking which of those dates are set. Order matters: they are
 * emitted in this sequence, not in whatever order the object happens to enumerate.
 *
 * `delivered` has two possible columns because there are two ways an order ends: the courier hands
 * it over (`receiptdate`) or the customer collects it from a pickup point (`selfpickdate`). They
 * are the same event to a customer, so they share one row rather than adding a stage nobody who
 * used home delivery will ever see.
 */
const TIMELINE_STAGES: readonly (readonly [OrderTimelineEvent['event'], readonly string[]])[] = [
  ['created', ['createdate']],
  ['weighedPacked', ['packdate']],
  ['paid', ['orderconfirmdate']],
  ['despatched', ['despatchdate']],
  ['delivered', ['receiptdate', 'selfpickdate']],
  ['cancelled', ['canceldate']],
];

function timelineFrom(record: Row): readonly OrderTimelineEvent[] {
  const events: OrderTimelineEvent[] = [];

  for (const [event, fields] of TIMELINE_STAGES) {
    for (const field of fields) {
      const at = toIsoTimestamp(record[field]);
      if (at) {
        events.push({ event, at });
        break;
      }
    }
  }

  return events;
}

/**
 * Where the order is going.
 *
 * Returns undefined when neither a recipient nor a street is set, which is how a `collecting`
 * order that has not been addressed yet renders "None" instead of a comma-separated line of blanks.
 */
function addressFrom(record: Row): DeliveryAddress | undefined {
  const recipient = prose(record['revname']);
  const line1 = prose(record['revaddr']);
  if (recipient === undefined && line1 === undefined) return undefined;

  return {
    recipient,
    // Two phone columns, both populated on real orders and not always with the same number. The
    // mobile is the one a courier calls from the door, so it goes first.
    phone: text(record['revphoneno']) ?? text(record['revtel']),
    line1,
    postcode: text(record['revzipcode']),
    // Names as the vendor resolved them (MALAYSIA / Johor / Kulai), codes as the cascade selects
    // them. Both are kept for the reason `DeliveryAddress` gives: the courier reads the names, and
    // reopening an order for editing needs the codes.
    country: prose(record['countryname']) ?? '',
    province: prose(record['provincename']),
    city: prose(record['cityname']) ?? '',
    countryCode: text(record['countryid']),
    provinceCode: text(record['provinceid']),
    cityCode: text(record['cityid']),
  };
}

/**
 * The packages on the order.
 *
 * `pkgInfo` is not a list of packages — it is the order-and-package join repeated once per
 * package, so every entry carries all 60-odd order columns again with the package's own columns
 * prefixed `pkg_`. Only the prefixed ones are read here; the rest are the same order this function
 * was called about.
 *
 * `pkg_declarename` is the customer's English declaration and `pkg_declarecnname` the Chinese one.
 * Either can be blank on its own, so whichever exists is used and the tracking number stands in
 * when neither does — an unnamed line in the panel is worse than a repeated number.
 */
function packagesFrom(record: Row): readonly OrderPackageLine[] {
  const raw = record['pkgInfo'];
  if (!Array.isArray(raw)) return [];

  const lines: OrderPackageLine[] = [];
  for (const entry of raw) {
    if (typeof entry !== 'object' || entry === null) continue;
    const pkg = entry as Row;
    const trackingNumber = text(pkg['pkg_inorderno']);
    if (trackingNumber === undefined) continue;

    const receiptPaths = receiptPathsFrom(pkg['imageurl']);
    lines.push({
      packageId: text(pkg['pkg_id']) ?? trackingNumber,
      trackingNumber,
      itemName: prose(pkg['pkg_declarename']) ?? prose(pkg['pkg_declarecnname']) ?? trackingNumber,
      weightGrams: grams(pkg['pkg_weight']),
      warehouseId: text(pkg['pkg_warehouseid']),
      goodsType: text(pkg['pkg_goodstype']),
      // `[""]` is how the vendor says "no receipts"; leave the field off rather than carry [].
      ...(receiptPaths.length > 0 ? { receiptPaths } : {}),
    });
  }

  return lines;
}

/** The ids of the add-on services on the order, for the picker to tick back on. */
function serviceIdsFrom(record: Row): readonly string[] {
  const raw = record['OrderService'];
  if (!Array.isArray(raw)) return [];

  const ids: string[] = [];
  for (const entry of raw) {
    if (typeof entry !== 'object' || entry === null) continue;
    const service = entry as Row;
    /*
      `sid` is the service's own id; `id` is this order's line (e.g. 567478 for service 6). Only
      `sid` is read. Falling back to `id` would put a line id into `orgOrderService`, and
      `order_modify` bills from the difference between that list and the new one — a line id
      there reads as a service to remove, and its real service as one to add. Checked live on
      2026-09-24: all 68 orders with services carry `sid`; the empty placeholder row
      `{servicename: ""}` has neither and is skipped.
    */
    const id = text(service['sid']);
    if (id !== undefined) ids.push(id);
  }

  return ids;
}

/** The order's services as it names them itself: `sid` + `servicename` (or `name`). */
function orderedServicesFrom(value: unknown): readonly { id: string; name: string }[] {
  if (!Array.isArray(value)) return [];

  const services: { id: string; name: string }[] = [];
  for (const entry of value) {
    if (typeof entry !== 'object' || entry === null) continue;
    const service = entry as Row;
    const id = text(service['sid']);
    const name = prose(service['servicename']) ?? prose(service['name']);
    if (id === undefined || name === undefined) continue;
    if (!services.some((known) => known.id === id)) services.push({ id, name });
  }

  return services;
}

/**
 * `PkgService` → `pid-sid` pairs, the format `order_modify` takes them back in.
 *
 * Live, the column is an empty string on an order with no package services and an array of rows
 * otherwise, each naming its package (`pid`) and service (`sid`). A row missing either is dropped:
 * a half pair would be a malformed entry in a list the vendor bills from.
 */
function packageServicesFrom(value: unknown): readonly string[] {
  if (!Array.isArray(value)) return [];

  const pairs: string[] = [];
  for (const entry of value) {
    if (typeof entry !== 'object' || entry === null) continue;
    const service = entry as Row;
    const packageId = text(service['pid']);
    const serviceId = text(service['sid']);
    if (packageId === undefined || serviceId === undefined) continue;
    const pair = `${packageId}-${serviceId}`;
    if (!pairs.includes(pair)) pairs.push(pair);
  }

  return pairs;
}

/**
 * What `order_modify` must be sent back unchanged (see `OrderModifyBase`).
 *
 * `ordertype` is required by the endpoint and has no safe default, so a record without it yields
 * no base at all — the order then cannot be saved, rather than saved as a guessed type. Every other
 * column falls back to what an absent value means on the wire: an empty string, and `"0"` (home
 * delivery) for the pickup point, which is how the endpoint itself reads an empty `pks`.
 */
function modifyBaseFrom(row: Row, statusCode: string): OrderModifyBase | undefined {
  const orderType = text(row['ordertype']);
  if (orderType === undefined) return undefined;

  return {
    statusCode,
    orderType,
    phoneNo: text(row['revphoneno']) ?? '',
    telNo: text(row['revtel']) ?? '',
    companyName: prose(row['revcompanyname']) ?? '',
    pickupSiteId: text(row['pickupsiteid']) ?? '0',
    sender: prose(row['sender']) ?? '',
    packageServices: packageServicesFrom(row['PkgService']),
  };
}

/**
 * The detail record (`order_list` with an `orderid`) → the fuller `SubmittedOrder`.
 *
 * Everything the summary row could not carry: the packages on the order, where it is going, when
 * each step happened, the remark, and how it gets paid. The caller replaces the summary with this
 * rather than merging field by field — every field the summary had is set here too, from the same
 * record, so there is nothing to preserve and nothing that can go stale against it.
 */
export function normaliseOrderDetail(
  record: unknown,
  statuses: readonly OrderStatusEntry[],
  language: OrderStatusLanguage,
): SubmittedOrder | undefined {
  if (typeof record !== 'object' || record === null) return undefined;
  const row = record as Row;

  const orderNumber = text(row['orderno']);
  if (orderNumber === undefined) return undefined;

  const statusCode = text(row['status']) ?? 'ORDERED';
  const trackingNumber = text(row['airno']);
  const carrier = prose(row['channelname']);
  const packages = packagesFrom(row);

  return {
    orderNumber,
    createdAt: toIsoTimestamp(row['createdate']) ?? '',
    status: toOrderStatus(statusCode),
    statusCode,
    statusLabel: statusLabelFor(row, statusCode, statuses, language),
    vendorOrderId: text(row['id']),
    packages,
    channelId: text(row['channelid']),
    carrier,
    // The waybill's weight if the order has one, otherwise what the packages themselves weigh —
    // which is all a `collecting` order can offer, and still worth showing.
    totalWeightGrams:
      grams(row['airno_weight']) ??
      packages.reduce((total, line) => total + (line.weightGrams ?? 0), 0),
    // `airno_calcweight` here, `calcweight` on a summary row: the same number under two names.
    chargeableWeightGrams: grams(row['airno_calcweight']),
    feeLines: [
      ...parseFeeLines(row['feename'], row['fee'], row['feecurrency']),
      ...serviceFeeLines(row['OrderService']),
    ],
    billedTotalCents: billedTotal(row['totalfee']),
    currency: text(row['currency']),
    address: addressFrom(row),
    tracking: trackingNumber ? { trackingNumber, carrier } : undefined,
    timeline: timelineFrom(row),
    serviceIds: serviceIdsFrom(row),
    orderedServices: orderedServicesFrom(row['OrderService']),
    warehouseId: text(row['warehouseid']),
    paymentMode: paymentMode(row['payment']),
    couponCode: text(row['codeno']) ?? '',
    remark: prose(row['remark']) ?? '',
    modifyBase: modifyBaseFrom(row, statusCode),
  };
}

/* -------------------------------------------------------------------------------------------- *
 * MYP-53 Slice 2 — the packages pool for the create/edit-order flow
 * -------------------------------------------------------------------------------------------- */

/**
 * A `Package` (the fuller record the Packages list shows) narrowed to what the order flow's
 * picker needs.
 *
 * `itemName` joins both declared-name fields rather than picking one, the same "often bilingual"
 * rendering `PackageRow`'s own doc comment describes — `package_list` sends the English and
 * Chinese declared names as two separate columns (`Package.itemNameEn`/`itemNameCn`), unlike an
 * order-detail row, which only ever carries one.
 */
export function toOrderablePackage(pkg: Package): OrderablePackage {
  return {
    id: pkg.id,
    trackingNumber: pkg.trackingNumber,
    itemName: pkg.itemNameCn ? `${pkg.itemNameCn} ${pkg.itemNameEn}` : pkg.itemNameEn,
    quantity: pkg.quantity,
    weightGrams: pkg.weightGrams,
    warehouseId: pkg.warehouseId || undefined,
    goodsType: pkg.goodsType || undefined,
  };
}

/**
 * The packages a new order can actually be built from: whichever of the account's packages carry
 * one of the vendor's own "orderable" status codes (`eligiblePackageStatusCodes`, from the
 * endpoint the ticket names as the source of truth) **and are not already on an order** (MYP-90),
 * converted to the picker's narrower shape.
 *
 * The status alone is not enough. A package keeps NEW or IN while its order is being put
 * together: live on 2026-09-25, 132 of the 162 packages with an orderable status were already on
 * an order (`outorderno` set), leaving 30 genuinely free — exactly the set `package_list`'s own
 * `validStatus=1` ("not yet ordered") returns once narrowed to these statuses. Both halves
 * together are the rule; `validStatus=1` alone also returns destroyed and returned parcels.
 *
 * Matched case-insensitively against `pkg.statusCode` — the vendor's raw code, not the six-bucket
 * `pkg.status` grouping the Packages list tabs by, since "orderable" is a finer distinction than
 * those six buckets draw (`in_warehouse` covers both a package just weighed and one already
 * flagged for a problem the warehouse hasn't resolved).
 */
export function eligibleOrderablePackages(
  packages: readonly Package[],
  eligibleStatusCodes: readonly string[],
): readonly OrderablePackage[] {
  const eligible = new Set(eligibleStatusCodes.map((code) => code.trim().toUpperCase()));
  return packages
    .filter(
      (pkg) =>
        eligible.has(pkg.statusCode.trim().toUpperCase()) &&
        (pkg.orderNumber === undefined || pkg.orderNumber.trim() === ''),
    )
    .map(toOrderablePackage);
}
